import type {
  BaseQueryFn,
  FetchBaseQueryError,
  MutationActionCreatorResult,
  MutationDefinition,
  QueryActionCreatorResult,
  QueryDefinition,
} from "@reduxjs/toolkit/query/react"
import type { Entity, Uuid } from "@/utils"

export type BaseQueryError = FetchBaseQueryError

type Query<QueryResult> = QueryDefinition<
  unknown,
  BaseQueryFn,
  string,
  QueryResult
>

type Mutation<MutationResult> = MutationDefinition<
  unknown,
  BaseQueryFn,
  string,
  MutationResult
>

export type ActionCreatorResult<Result> =
  | QueryActionCreatorResult<Query<Result>>
  | MutationActionCreatorResult<Mutation<Result>>

/** A page of a cursor-paged list; `hasPrev` says whether rows lie before its first item, exactly so except on pages fetched with a forward cursor, which always say true. */
export type CursorPage<TItem> = {
  items: TItem[]
  nextCursor: string | null
  prevCursor: string | null
  hasPrev: boolean
}

export type RealtimeHeader = {
  channel: string
  event: string
}

export type EntitiesWithRealtime<TEntity extends Entity> = {
  items: TEntity[]
} & RealtimeHeader

export type StartEndPager = {
  start: number
  end: number
}

export type DateRange = {
  from?: Date
  to?: Date
}

// Realtime (Ably) types

export const REALTIME_HEADER = "Realtime"

export enum Operation {
  Insert = "insert",
  Update = "update",
  Delete = "delete",
}

type Insert<TEntity extends Entity> = {
  readonly operation: Operation.Insert
  readonly payload: TEntity
}

type Update<TEntity extends Entity> = {
  readonly operation: Operation.Update
  readonly payload: TEntity
}

type Remove = {
  readonly operation: Operation.Delete
  readonly payload: Entity["id"]
}

export type EntityChange<TEntity extends Entity> = { actorId: Uuid | null } & (
  Insert<TEntity> | Update<TEntity> | Remove
)
